/**
 * The manager view's KPI cards — phishing, training and announcements — read
 * SQL totals the API sends, never a sum of list rows (audit 2026-09-25 batch
 * B, T20 remainder; "reports ship answers, not rows").
 *
 * The page used to add up the campaign, course and announcement rows the
 * manager had ticked. Those lists stop at 500 rows, so a team with more than
 * that was shown a KPI of the newest 500. Now:
 *
 *  - every row of a list ticked (the default) → the dashboard's own
 *    `kpiTotals`, which count ALL of them, including any past the 500 listed;
 *  - some rows ticked → GET …/kpis with those ids, which the API totals in SQL;
 *  - nothing ticked → nothing to count (zeros).
 *
 * The shapes are the API's (TeamKpiTotalsResponseDto).
 */

export interface PhishingKpis {
  sent: number
  opened: number
  openedRate: number
  clicked: number
  clickedRate: number
  compromised: number
  compromisedRate: number
  reported: number
  reportedRate: number
  trained: number
  trainedRate: number
  evaluated: number
  evaluatedRate: number
}

export interface TrainingKpis {
  assigned: number
  completed: number
  completionRate: number
  inProgress: number
  notStarted: number
  overdue: number
}

export interface AnnouncementKpis {
  sent: number
  opened: number
  openedRate: number
  acknowledged: number
  acknowledgedRate: number
  trained: number
  trainedRate: number
}

export interface TeamKpiTotals {
  phishing: PhishingKpis
  training: TrainingKpis
  announcements: AnnouncementKpis
}

export const EMPTY_PHISHING_KPIS: PhishingKpis = {
  sent: 0, opened: 0, openedRate: 0, clicked: 0, clickedRate: 0, compromised: 0, compromisedRate: 0,
  reported: 0, reportedRate: 0, trained: 0, trainedRate: 0, evaluated: 0, evaluatedRate: 0,
}
export const EMPTY_TRAINING_KPIS: TrainingKpis = {
  assigned: 0, completed: 0, completionRate: 0, inProgress: 0, notStarted: 0, overdue: 0,
}
export const EMPTY_ANNOUNCEMENT_KPIS: AnnouncementKpis = {
  sent: 0, opened: 0, openedRate: 0, acknowledged: 0, acknowledgedRate: 0, trained: 0, trainedRate: 0,
}

/** One list's ticks: which ids are selected out of how many rows it shows. */
export interface KpiSelection {
  selectedIds: string[]
  listed: number
}

/** A selection that narrows its list: some ticked, not all. */
function narrows(sel: KpiSelection): boolean {
  return sel.selectedIds.length > 0 && sel.selectedIds.length < sel.listed
}

/**
 * The query string for GET …/kpis, or `null` when no list is narrowed (the
 * dashboard's `kpiTotals` already answer). Ids are sorted so the same ticks
 * always make the same request.
 */
export function teamKpiQuery(sel: {
  campaigns: KpiSelection
  courses: KpiSelection
  announcements: KpiSelection
}): string | null {
  const params: string[] = []
  const add = (name: string, s: KpiSelection) => {
    if (narrows(s)) params.push(`${name}=${[...s.selectedIds].sort().map(encodeURIComponent).join(',')}`)
  }
  add('campaignIds', sel.campaigns)
  add('trainingIds', sel.courses)
  add('announcementIds', sel.announcements)
  return params.length > 0 ? params.join('&') : null
}
